import { BaseRoutingStrategy } from './BaseRoutingStrategy';

export class GoogleRoutingStrategy extends BaseRoutingStrategy {
  constructor(apiKey) {
    super(apiKey);
    this.autocomplete = null;
  }

  async calculateRoute({
    waypoints = [],
    avoidTolls = false,
    avoidHighways = false,
    avoidFerries = false,
    optimize = false
  } = {}) {
    if (!window.google?.maps) {
      throw new Error('Google Maps non inizializzata');
    }

    if (waypoints.length < 2) {
      throw new Error('Origine o destinazione non valide');
    }

    const getWaypointLocation = waypoint => {
      const lat = parseFloat(waypoint.lat);
      const lng = parseFloat(waypoint.lng);
      return Number.isFinite(lat) && Number.isFinite(lng)
        ? { lat, lng }
        : waypoint.address;
    };

    const origin = getWaypointLocation(waypoints[0]);
    const destination = getWaypointLocation(waypoints[waypoints.length - 1]);
    if (!origin || !destination) {
      throw new Error('Origine o destinazione non valide');
    }

    const intermediateWaypoints = waypoints.slice(1, -1)
      .map(waypoint => ({
        location: getWaypointLocation(waypoint),
        stopover: true
      }))
      .filter(waypoint => waypoint.location);

    const request = {
      origin,
      destination,
      waypoints: intermediateWaypoints,
      optimizeWaypoints: optimize,
      avoidTolls,
      avoidHighways,
      avoidFerries,
      travelMode: window.google.maps.TravelMode.DRIVING,
      region: 'IT'
    };

    const service = new window.google.maps.DirectionsService();
    const result = await new Promise((resolve, reject) => {
      service.route(request, (response, status) => {
        if (status === window.google.maps.DirectionsStatus.OK && response) {
          resolve(response);
          return;
        }

        reject(new Error(`Errore calcolo percorso: ${status}`));
      });
    });

    const route = result.routes?.[0];
    if (!route) {
      throw new Error('Google Maps non ha restituito alcun percorso');
    }

    const distanceMeters = route.legs.reduce(
      (total, leg) => total + (leg.distance?.value || 0),
      0
    );
    const durationSeconds = route.legs.reduce(
      (total, leg) => total + (leg.duration?.value || 0),
      0
    );
    const durationHours = Math.floor(durationSeconds / 3600);
    const durationMinutes = Math.floor((durationSeconds % 3600) / 60);
    let optimizedWaypoints = null;

    if (optimize && route.waypoint_order?.length > 0) {
      const reorderedIntermediate = route.waypoint_order.map(index => waypoints[index + 1]);
      optimizedWaypoints = [
        waypoints[0],
        ...reorderedIntermediate,
        waypoints[waypoints.length - 1]
      ];
    }

    return {
      provider: 'google',
      raw: result,
      route,
      distanceMeters,
      durationSeconds,
      distanceText: `${(distanceMeters / 1000).toFixed(1)} km`,
      durationText: durationHours > 0
        ? `${durationHours}h ${durationMinutes}m`
        : `${durationMinutes} min`,
      optimizedWaypoints
    };
  }

  preloadApi() {
    if (window.google && window.google.maps) return;
    if (document.getElementById('generazione-itinerari-maps-script')) return;
    
    if (!this.apiKey) return;
    
    const script = document.createElement('script');
    script.id = 'generazione-itinerari-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places,geometry&loading=async`;
    script.async = true;
    document.head.appendChild(script);
  }

  async geocodeWaypoints(waypoints) {
    return new Promise((resolve) => {
      if (!window.google || !window.google.maps) {
        console.warn('[GoogleRoutingStrategy] Google Maps not available');
        resolve();
        return;
      }

      const needsGeocoding = waypoints.filter(wp => wp.needsGeocoding && wp.address);
      
      if (needsGeocoding.length === 0) {
        resolve();
        return;
      }

      const geocoder = new google.maps.Geocoder();
      let completed = 0;

      needsGeocoding.forEach(wp => {
        geocoder.geocode({ address: wp.address }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const loc = results[0].geometry.location;
            wp.lat = loc.lat();
            wp.lng = loc.lng();
            console.log('[GoogleRoutingStrategy] Geocoded:', wp.address, 'to', wp.lat, wp.lng);
          } else {
            console.warn('[GoogleRoutingStrategy] Geocoding failed:', wp.address, status);
          }

          completed++;
          if (completed === needsGeocoding.length) {
            resolve();
          }
        });
      });
    });
  }

  async geocodeAddress(address) {
    return new Promise((resolve) => {
      if (!window.google || !window.google.maps) {
        resolve(null);
        return;
      }

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          resolve({
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng()
          });
        } else {
          console.error('[GoogleRoutingStrategy] Geocoding failed:', status);
          resolve(null);
        }
      });
    });
  }

  initAutocomplete(inputElement, onPlaceSelected) {
    if (!inputElement || !window.google || !google.maps || !google.maps.places) {
      console.warn("[GoogleRoutingStrategy] Google Maps Places library not ready");
      setTimeout(() => this.initAutocomplete(inputElement, onPlaceSelected), 200);
      return;
    }

    const options = {
      componentRestrictions: { country: ["it", "fr", "ch"] },
      fields: ["formatted_address", "geometry", "name"],
      types: ["establishment", "geocode"],
    };

    this.autocomplete = new google.maps.places.Autocomplete(inputElement, options);

    inputElement.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') e.preventDefault();
    });

    this.autocomplete.addListener("place_changed", () => {
      const place = this.autocomplete.getPlace();
      if (onPlaceSelected) {
        onPlaceSelected(place);
      }
    });
  }

  initOriginAutocomplete(inputElement, onPredictionsUpdated) {
    if (!inputElement) return;

    let debounceTimer;

    inputElement.addEventListener('input', (e) => {
      clearTimeout(debounceTimer);
      const value = e.target.value;

      if (value.length < 2) {
        onPredictionsUpdated([]);
        return;
      }

      debounceTimer = setTimeout(async () => {
        try {
          const request = {
            input: value,
            componentRestrictions: { country: ["it", "fr", "ch"] },
            language: "it",
          };

          const service = new google.maps.places.AutocompleteService();

          service.getPlacePredictions(request, (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
              onPredictionsUpdated(predictions);
            } else {
              onPredictionsUpdated([]);
            }
          });
        } catch (error) {
          console.error('[GoogleRoutingStrategy] Origin autocomplete error:', error);
        }
      }, 300);
    });

    inputElement.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') e.preventDefault();
    });

    inputElement.focus();
  }

  initDestinationAutocomplete(inputElement, onPredictionsUpdated) {
    if (!inputElement) return;

    let debounceTimer;

    inputElement.addEventListener('input', (e) => {
      clearTimeout(debounceTimer);
      const value = e.target.value;

      if (value.length < 2) {
        onPredictionsUpdated([]);
        return;
      }

      debounceTimer = setTimeout(async () => {
        try {
          const request = {
            input: value,
            componentRestrictions: { country: ["it", "fr", "ch"] },
            language: "it",
          };

          const service = new google.maps.places.AutocompleteService();

          service.getPlacePredictions(request, (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
              onPredictionsUpdated(predictions);
            } else {
              onPredictionsUpdated([]);
            }
          });
        } catch (error) {
          console.error('[GoogleRoutingStrategy] Destination autocomplete error:', error);
        }
      }, 300);
    });

    inputElement.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') e.preventDefault();
    });

    inputElement.focus();
  }

  buildDeepLink(waypoints, options = {}) {
    if (!Array.isArray(waypoints) || waypoints.length < 2) return null;

    const getLoc = (wp) => {
      if (wp.lat != null && wp.lng != null) return `${wp.lat},${wp.lng}`;
      return encodeURIComponent(wp.address || '');
    };

    const origin = getLoc(waypoints[0]);
    const destination = getLoc(waypoints[waypoints.length - 1]);
    const intermediateStops = waypoints.slice(1, -1);

    let link = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;

    if (intermediateStops.length > 0) {
      const stops = intermediateStops.map(wp => getLoc(wp)).join('%7C');
      link += `&waypoints=${stops}`;
    }

    return link;
  }

  async selectPrediction(prediction) {
    return new Promise((resolve) => {
      const service = new google.maps.places.PlacesService(document.createElement('div'));

      service.getDetails({
        placeId: prediction.place_id,
        fields: ["formatted_address", "geometry", "name"],
      }, (place, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place.geometry) {
          resolve(null);
          return;
        }

        resolve({
          label: place.name || place.formatted_address,
          address: place.formatted_address,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        });
      });
    });
  }
}
